import {lazy, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonColors, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMountedState, useOrientation} from '@s-ui/react-hooks'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import {NavigationLastAdsSearchesModal, useLastAdsSearches} from '@adv-mt/mt-navigation-last-ads-searches'
import NotificationAi from '@adv-mt/mt-notifications-ai'
import SearchAiQuickFilters, {
  SearchAiQuickFiltersModalContent,
  SearchAiQuickFiltersModalFooter,
  useAiSearch
} from '@adv-mt/mt-search-ai-quick-filters'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {
  BASE_CLASS,
  DESIGNS as searchFiltersButtonDesigns,
  SEARCH_FILTERS_PLACES,
  SEARCH_FILTERS_PLACES_DICTIONARY,
  SEARCH_USE_CASE_BY_PLACE
} from './config.js'
import CustomRenderActions from './customRenderActions.js'
import FilterButton from './filterButton.js'

const FiltersModal = lazy(() => import(/* webpackChunkName: "SearchFiltersModal" */ './modal.js'))

const ListFilters = lazy(() =>
  Promise.all([
    import(/* webpackChunkName: "SearchFiltersListFilters" */ '@adv-mt/mt-list-filters/src/index.js'),
    import(/* webpackChunkName: "SearchFiltersListFiltersStyles" */ '@adv-mt/mt-list-filters/src/index.scss')
  ]).then(([Component]) => Component)
)

const NavigationLastAdsSearches = lazy(() =>
  Promise.all([
    import(
      /* webpackChunkName: "SearchFiltersNavigationLastAdsSearches" */ '@adv-mt/mt-navigation-last-ads-searches/src/index.js'
    ),
    import(
      /* webpackChunkName: "SearchFiltersNavigationLastAdsSearchesStyles" */ '@adv-mt/mt-navigation-last-ads-searches/src/index.scss'
    )
  ]).then(([Component]) => Component)
)

const DEFAULT_GET_SEARCH_RESULTS_USE_CASE = 'get_search_results_ads_use_case'
const DEFAULT_GET_SEARCH_URL_USE_CASE = 'get_search_url_ads_use_case'
const FILTER_ADDED_EVENT = 'Filter Added'
const FILTER_ADDED_ORIGIN = 'Filters Filter Added'
const SHOW_SEARCH_RESULTS_BUTTON_CLICKED_EVENT = 'Show Search Results Button Clicked'
const FILTER_ZERO_RESULT_ICON_VIEWED_EVENT = 'Filter 0 Result Icon Viewed'
const OVERFLOW_SCROLLING_TIMEOUT = 10

const SearchFilters = ({
  aggregations,
  customButtonText,
  filtersConfig = [],
  dealerSlug,
  design = searchFiltersButtonDesigns.BASIC,
  filtersProps = {},
  listingSectionId,
  search,
  initialShow = false,
  pageName,
  place,
  totalResults,
  withAiQuickFilters = false,
  showSearchSave = true
}) => {
  const orientation = useOrientation()
  const [showFiltersModal, setShowFiltersModal] = useState(initialShow)
  const [showAiQuickFiltersModal, setShowAiQuickFiltersModal] = useState(false)
  const [currentLastFilter, setCurrentLastFilter] = useState()
  const [tempTotalResults, setTempTotalResults] = useState(totalResults)
  const [currentAggregations, setCurrentAggregations] = useState(aggregations)
  const [isLoading, setIsLoading] = useState(false)
  const [currentSearch, setCurrentSearch] = useState(search)

  const {lastAdsSearches, isLastAdsSearchesModalOpen, onCloseLastAdsSearchesModal, onOpenLastAdsSearchesModal} =
    useLastAdsSearches()

  useEffect(() => {
    setCurrentSearch(search)
  }, [search])

  const delayToNextFrame = useDelayToNextFrame()

  const {
    isLoading: isLoadingAiSearch,
    onSearch: onSearchAi,
    notificationType,
    onCloseNotification
  } = useAiSearch({
    isLoading,
    listingSectionId,
    pageName
  })

  const {track} = useAnalyticsTrack()
  const {domain, i18n} = useContext(Context)
  const getMountedState = useMountedState()

  const hasAggregations = Boolean(aggregations)

  const isMounted = getMountedState()

  const config = domain.get('config')

  const hasResults = Boolean(tempTotalResults)

  const {
    ADS_LIST_PAGE_NAME: adsListPageName,
    ADS_LIST_SUBSCRIPTION_PAGE_NAME: adsListSubscriptionPageName = '',
    MAKES_DISTRIBUTOR_PAGE_NAME: modelListPageName = '',
    PROFESSIONAL_STOCK_PAGE_NAME: professionalStockPageName = '',
    TECHNICAL_DETAILS_SEARCH_PAGE_NAME: technicalDetailsSearchPageName,
    TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME: technicalDetailsSearchResultsPageName
  } = config.get('page')

  const {categoryId, offerTypeId} = currentSearch

  const getSearchResultsUseCaseMapper = {
    [adsListPageName]: DEFAULT_GET_SEARCH_RESULTS_USE_CASE,
    [professionalStockPageName]: DEFAULT_GET_SEARCH_RESULTS_USE_CASE,
    [adsListSubscriptionPageName]: DEFAULT_GET_SEARCH_RESULTS_USE_CASE,
    [technicalDetailsSearchPageName]: 'get_search_results_vehicles_use_case',
    [technicalDetailsSearchResultsPageName]: 'get_search_results_vehicles_use_case'
  }

  const getSearchUrlUseCaseMapper = {
    [adsListSubscriptionPageName]: 'get_url_params_search_use_case',
    [technicalDetailsSearchPageName]: 'get_search_url_vehicles_use_case',
    [technicalDetailsSearchResultsPageName]: 'get_search_url_vehicles_use_case'
  }

  const isTechnicalDetailsPage =
    pageName === technicalDetailsSearchPageName || pageName === technicalDetailsSearchResultsPageName

  const isModelsListPage = pageName === modelListPageName

  const getSearchUrlUseCaseName = getSearchUrlUseCaseMapper[pageName] || DEFAULT_GET_SEARCH_URL_USE_CASE

  useEffect(() => {
    showFiltersModal &&
      domain
        .get('get_page_tracking_data_use_case')
        .execute({
          page: 'ads-list-filters',
          pageOrigin: pageName,
          search: {categoryId, offerTypeId}
        })
        .then(({event, data} = {}) => {
          event && track({event, data})
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFiltersModal])

  const openModal = () => setShowFiltersModal(true)
  const closeModal = () => setShowFiltersModal(false)
  const closeQuickFiltersModal = () => setShowAiQuickFiltersModal(false)

  const _onAddFilterTrack = ({search: updatedSearch, lastFilter: lastFilterAdded, withFilterAddedEvent}) => {
    domain
      .get('get_page_tracking_data_use_case')
      .execute({
        page: pageName || adsListPageName,
        search: updatedSearch,
        lastFilter: lastFilterAdded
      })
      .then(({data} = {}) => {
        data.last_filter &&
          withFilterAddedEvent &&
          track({
            event: FILTER_ADDED_EVENT,
            data: {
              hit_origin: FILTER_ADDED_ORIGIN,
              last_filter: data.last_filter
            }
          })
      })
  }

  const handleOpenModal = () => openModal()

  const handleCloseModal = () => {
    closeModal()
  }

  const _getResultsCount = search => {
    const getSearchResultsUseCaseName = getSearchResultsUseCaseMapper[pageName] || DEFAULT_GET_SEARCH_RESULTS_USE_CASE

    const useCaseParams = isTechnicalDetailsPage ? {search} : search

    const useCasePromise = domain
      .get(getSearchResultsUseCaseName)
      .execute({...useCaseParams, listingSectionId, withAggregations: hasAggregations})

    if (isTechnicalDetailsPage) {
      useCasePromise
        .then(([error, response]) => {
          if (error) return
          setTempTotalResults(response.meta.totalResults)
        })
        .finally(() => setIsLoading(false))
    } else {
      useCasePromise
        .then(([error, response]) => {
          if (error) return
          !response.totalResults &&
            track({
              event: FILTER_ZERO_RESULT_ICON_VIEWED_EVENT
            })
          setTempTotalResults(response.totalResults)
          hasAggregations && setCurrentAggregations(response.aggregations)
        })
        .finally(() => setIsLoading(false))
    }
  }

  const handleOnFilter = ({search, lastFilter, withFilterAddedEvent}) => {
    setIsLoading(true)
    delayToNextFrame(() => {
      _getResultsCount(search)
      setCurrentSearch(search)
      setCurrentLastFilter(lastFilter)
      lastFilter && _onAddFilterTrack({search, lastFilter, withFilterAddedEvent})
    })
  }

  const handleShowResults = () => {
    closeModal()
    delayToNextFrame(() => {
      domain
        .get(
          // Progressive rollout form new get url params search use case
          SEARCH_USE_CASE_BY_PLACE[place] || getSearchUrlUseCaseName
        )
        .execute({
          dealerSlug,
          lastFilter: currentLastFilter,
          listingSectionId,
          pageName,
          search: currentSearch
        })
        .then(() => {
          track({
            event: SHOW_SEARCH_RESULTS_BUTTON_CLICKED_EVENT
          })
        })
    })
  }

  useEffect(() => {
    if (!showFiltersModal) return

    setTempTotalResults(totalResults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFiltersModal])

  /** This useEffect solves a Safari mobile bug
   * See video on readme or https://jira.scmspain.com/browse/MTR-35308
   */
  useEffect(() => {
    const modalContents = document.querySelectorAll('.sui-MoleculeModal-content')

    modalContents.forEach(modalContent => {
      modalContent.style['-webkit-overflow-scrolling'] = 'auto'
      setTimeout(() => {
        modalContent.style['-webkit-overflow-scrolling'] = 'touch'
      }, OVERFLOW_SCROLLING_TIMEOUT)
    })
  }, [orientation])

  if (isModelsListPage) return <></>

  const buttonText = hasResults ? `${i18n.t('COMMON.SHOW')} ${i18n.n(tempTotalResults)}` : i18n.t('COMMON.NO_RESULTS')

  return (
    <div className={BASE_CLASS}>
      <FilterButton
        customButtonText={customButtonText}
        onClick={handleOpenModal}
        design={design}
        pageName={pageName}
        search={currentSearch}
      />
      {isMounted && showFiltersModal && (
        <Suspense fallback={<></>}>
          <FiltersModal
            customFooter={
              <div className={`${BASE_CLASS}-cta`}>
                <CustomRenderActions
                  search={currentSearch}
                  showSearchSave={showSearchSave}
                  onAccept={handleShowResults}
                  acceptButtonText={buttonText}
                  acceptButtonOptions={{
                    color: atomButtonColors.PRIMARY,
                    fullWidth: true,
                    shape: atomButtonShapes.CIRCULAR,
                    isFitted: true,
                    disabled: !hasResults
                  }}
                  isSearchModal={true}
                  hasZeroResults={!hasResults}
                  isLoading={isLoading}
                />
              </div>
            }
            dealerSlug={dealerSlug}
            isOpen={showFiltersModal}
            isTechnicalDetailsPage={isTechnicalDetailsPage}
            listingSectionId={listingSectionId}
            onClose={handleCloseModal}
            onRemoveFilter={handleOnFilter}
            onReset={handleOnFilter}
            pageName={pageName}
            place={place}
            search={currentSearch}
          >
            {currentSearch && (
              <div className={`${BASE_CLASS}-content`}>
                {!isTechnicalDetailsPage && (
                  <div className={`${BASE_CLASS}-lastSavedSearches`}>
                    <NavigationLastAdsSearches
                      lastAdsSearches={lastAdsSearches}
                      onOpenModal={onOpenLastAdsSearchesModal}
                    />
                  </div>
                )}

                {withAiQuickFilters && (
                  <SearchAiQuickFilters
                    isLoading={isLoadingAiSearch}
                    onSearch={onSearchAi}
                    onShowResults={handleCloseModal}
                    onOpenModal={() => setShowAiQuickFiltersModal(true)}
                  />
                )}

                <ListFilters
                  aggregations={currentAggregations}
                  config={filtersConfig}
                  filtersProps={filtersProps}
                  hasUserInteractionWithFilters={showFiltersModal}
                  listingSectionId={listingSectionId}
                  onFilter={handleOnFilter}
                  pageName={pageName}
                  place={SEARCH_FILTERS_PLACES_DICTIONARY[place]}
                  search={currentSearch}
                />
              </div>
            )}
          </FiltersModal>
        </Suspense>
      )}

      {showAiQuickFiltersModal && (
        <Suspense fallback={<></>}>
          <ModalBasic
            isOpen
            size={moleculeModalSizes.LARGE}
            title={i18n.t('SEARCH_AI.FILTERS.LABEL')}
            onClose={closeQuickFiltersModal}
            className={`${BASE_CLASS}-aiQuickFiltersModal`}
            customFooter={
              <SearchAiQuickFiltersModalFooter isLoading={isLoadingAiSearch} onCloseModal={closeQuickFiltersModal} />
            }
          >
            <div style={{flex: 1}}>
              <SearchAiQuickFiltersModalContent
                isLoading={isLoadingAiSearch}
                onSearch={onSearchAi}
                onShowResults={handleCloseModal}
                onCloseModal={closeQuickFiltersModal}
              />
            </div>
          </ModalBasic>
        </Suspense>
      )}

      {!isTechnicalDetailsPage && (
        <Suspense fallback={<></>}>
          <NavigationLastAdsSearchesModal
            isOpen={isLastAdsSearchesModalOpen}
            onClose={onCloseLastAdsSearchesModal}
            onOpen={onOpenLastAdsSearchesModal}
            lastAdsSearches={lastAdsSearches}
          />
        </Suspense>
      )}

      {Boolean(notificationType) && !isLoadingAiSearch && (
        <NotificationAi onClose={onCloseNotification} type={notificationType} />
      )}
    </div>
  )
}

SearchFilters.displayName = 'SearchFilters'
SearchFilters.propTypes = {
  aggregations: PropTypes.array,
  customButtonText: PropTypes.string,
  dealerSlug: PropTypes.string,
  design: PropTypes.oneOf(Object.keys(searchFiltersButtonDesigns)),
  filtersConfig: PropTypes.array,
  filtersProps: PropTypes.object,
  initialShow: PropTypes.bool,
  listingSectionId: PropTypes.string,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object.isRequired,
  totalResults: PropTypes.number,
  withAiQuickFilters: PropTypes.bool,
  showSearchSave: PropTypes.bool
}

export {searchFiltersButtonDesigns, CustomRenderActions, SEARCH_FILTERS_PLACES as searchFiltersPlaces}

export default SearchFilters
